import React from "react";
import { Modal, Button } from "react-bootstrap";
import { Home, User, UserPlus, Edit3, Shield, Bell, MapPin, Clock, Navigation, Award, FileText } from "react-feather";
import CollapsibleCard, { PANEL_ACCENT } from "../../components/CollapsibleCard";
import EmployeeAvatar from "../../../reports/oneStop/components/EmployeeAvatar";
import { ClientVisitPrintButton } from "./ClientVisitPrint";

/*
  One recorded client visit, read-only - laid out to read like the paper
  "CLIENT VISIT REPORT" it replaces (header, 1. GUARDING, 2. ALARM, client
  remarks).

  No edit and no delete, deliberately: a visit is a dated statement about what
  somebody saw on site. Correcting one means recording another, same as the
  append-only customer field-visit history.

  Styled with the house `CollapsibleCard` (see feedback_detail_panels_colour_by_data):
  each panel carries its own accent, a count pill and a one-line summary, and a
  panel with nothing in it goes grey + collapsed rather than showing a page of
  blank rows. So a remarks-only visit shows grey Guarding/Alarm panels that say
  so, instead of implying everything was assessed and found empty.
*/

const RATING_LABEL = { GOOD: "Good", AVERAGE: "Average", POOR: "Poor" };
const COVERAGE_LABEL = { STANDARD: "Standard", UNDER_DEPLOYMENT: "Under Deployment" };
const RATING_TONE = {
  GOOD: { fg: "#1E8449", bg: "#E8F5EE" },
  AVERAGE: { fg: "#B26A00", bg: "#FDF1DF" },
  POOR: { fg: "#C0392B", bg: "#FCE9E9" },
};

// Never rely on an inherited `color` for text on a tinted panel. The vendored
// template has a real typo - `bg-light { color: $text-color }` in
// style/scss/base/_background.scss is missing its leading dot, so Bootstrap's
// .bg-light gets its background but no text colour, and inside a Modal that
// left the remarks white-on-white (reported live, 2026-09-18). Every readable
// surface below sets its own foreground explicitly.
const INK = "#1F2A37";
const INK_MUTED = "#667085";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Field = ({ label, children, width = "col-md-4" }) => (
  <div className={`${width} mb-2`}>
    <div style={{ fontSize: 11, color: INK_MUTED, letterSpacing: 0.2 }}>{label}</div>
    <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
      {children ?? <span style={{ color: INK_MUTED, fontWeight: 400 }}>—</span>}
    </div>
  </div>
);

const Rating = ({ value }) => {
  if (!value) return <span style={{ color: INK_MUTED, fontWeight: 400 }}>Not assessed</span>;
  const tone = RATING_TONE[value] || { fg: INK_MUTED, bg: "#F2F4F7" };
  return (
    <span
      style={{
        fontSize: 11.5, fontWeight: 800, color: tone.fg, background: tone.bg,
        border: `1px solid ${tone.fg}40`, borderRadius: 999, padding: "2px 10px",
      }}
    >
      {RATING_LABEL[value] || value}
    </span>
  );
};

// A count that matters, in the brand colour rather than muted grey.
const BigCount = ({ label, value, color = PANEL_ACCENT.guards }) => (
  <div className="col-6 col-md-3 mb-2">
    <div style={{ fontSize: 11, color: INK_MUTED }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: value ? color : INK_MUTED, lineHeight: 1.2 }}>
      {value ?? "—"}
    </div>
  </div>
);

// Free text gets its own readable block - explicit ink and background, never
// an inherited colour (see INK above).
const TextBlock = ({ children, tint }) => (
  <div
    style={{
      whiteSpace: "pre-wrap",
      fontSize: 13.5,
      color: INK,
      background: tint || "#F9FAFB",
      border: "1px solid #E4E7EC",
      borderRadius: 8,
      padding: "8px 10px",
    }}
  >
    {children}
  </div>
);

// `photoUrl` comes from the page-level useEmployeePhotoCache (one fetch per
// employee for the whole screen); undefined = in flight, null = no photo, and
// EmployeeAvatar renders initials for either.
const ClientVisitDetailModal = ({ visit, photoUrl, onClose }) => {
  if (!visit) return null;

  const devices = [
    visit.hasManualAlarm ? "Manual Alarm" : null,
    visit.hasAutoAlarm ? "A/Alarm" : null,
    visit.hasElectricFence ? "Electric Fence" : null,
    visit.hasFireAlarm ? "Fire Alarm" : null,
    visit.hasCctv ? "C.C.T.V" : null,
  ].filter(Boolean);

  const guardCounts = [visit.guardsDay, visit.guardsNight, visit.dogAndHandler].filter((n) => n != null);
  const totalGuards = guardCounts.length ? guardCounts.reduce((a, b) => a + b, 0) : null;

  const hasChecklist = Boolean(visit.hasOperationsChecklist);
  const hasGuarding =
    hasChecklist &&
    [visit.guardsDay, visit.guardsNight, visit.dogAndHandler, visit.guardInChargeName,
      visit.uniformRating, visit.appearanceRating, visit.presentationRating,
      visit.guardingCoverage, visit.guardingComments].some((v) => v != null && v !== "");
  const hasAlarm =
    hasChecklist &&
    (devices.length > 0 ||
      [visit.alarmOthers, visit.installationQualityRating, visit.alarmCoverageRating, visit.alarmComments]
        .some((v) => v != null && v !== ""));

  // Batch212 - a new customer (prospect) and the requirement for quotation.
  const isProspect = Boolean(visit.isProspect);
  const needs = [
    visit.needsGuarding ? "Guarding" : null,
    visit.needsAlarms ? "Alarms" : null,
    visit.needsOther ? "Other" : null,
  ].filter(Boolean);
  const hasRequirement = needs.length > 0 || Boolean(visit.requirementDetails);

  const hasContact = Boolean(visit.contactPerson || visit.contactDesignation || visit.contactMobile);
  const hasCoords = visit.latitude != null && visit.longitude != null;
  const mapUrl = hasCoords ? `https://www.google.com/maps?q=${visit.latitude},${visit.longitude}` : null;

  const poorFlags = [visit.uniformRating, visit.appearanceRating, visit.presentationRating,
    visit.installationQualityRating, visit.alarmCoverageRating].filter((r) => r === "POOR").length;

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      {/* Soft gradient header so the customer reads as a real header rather
          than another row - same treatment as the Flutter detail screens. */}
      <Modal.Header
        closeButton
        style={{
          background: `linear-gradient(135deg, ${PANEL_ACCENT.document}1A, ${PANEL_ACCENT.document}05)`,
          borderBottom: `1px solid ${PANEL_ACCENT.document}26`,
        }}
      >
        <Modal.Title as="div" className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 42, height: 42, background: `${PANEL_ACCENT.document}1F` }}
          >
            <Home size={20} color={PANEL_ACCENT.document} />
          </span>
          <span style={{ minWidth: 0 }}>
            {/* Leads with the customer, not the CV- number. */}
            <div className="text-truncate" style={{ fontSize: 17, fontWeight: 800, color: PANEL_ACCENT.document }}>
              {visit.custName}
              {visit.siteLabel ? <span style={{ color: INK_MUTED, fontWeight: 600 }}> — {visit.siteLabel}</span> : null}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 12, color: INK_MUTED }}>
              <span>{visit.visitNumber}</span>
              <span>·</span>
              <span className="d-inline-flex align-items-center gap-1">
                <Clock size={11} /> {formatDateTime(visit.visitedAt)}
              </span>
              {isProspect && (
                <span
                  style={{
                    fontSize: 10.5, fontWeight: 800, color: PANEL_ACCENT.link,
                    background: `${PANEL_ACCENT.link}1A`, borderRadius: 999, padding: "1px 8px",
                  }}
                >
                  NEW CUSTOMER
                </span>
              )}
              {hasChecklist && (
                <span
                  style={{
                    fontSize: 10.5, fontWeight: 800, color: PANEL_ACCENT.money,
                    background: `${PANEL_ACCENT.money}1A`, borderRadius: 999, padding: "1px 8px",
                  }}
                >
                  CHECKLIST
                </span>
              )}
              {poorFlags > 0 && (
                <span
                  style={{
                    fontSize: 10.5, fontWeight: 800, color: "#C0392B",
                    background: "#FCE9E9", borderRadius: 999, padding: "1px 8px",
                  }}
                >
                  {poorFlags} POOR
                </span>
              )}
            </div>
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#F7F8FA" }}>
        <CollapsibleCard
          title="Visit"
          icon={User}
          accent={PANEL_ACCENT.neutral}
          hasData
          summary={`${visit.visitedByName || "Unknown"} · ${visit.branchCode || ""}`}
        >
          <div className="d-flex align-items-center gap-2 mb-2">
            <EmployeeAvatar name={visit.visitedByName} photoUrl={photoUrl} size="lg" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>{visit.visitedByName}</div>
              <div style={{ fontSize: 11.5, color: INK_MUTED }}>
                {visit.visitedByEmployeeCode ? `Employee ${visit.visitedByEmployeeCode}` : "Visited this customer"}
              </div>
            </div>
          </div>
          <div className="row">
            <Field label="Branch">{visit.branchCode}</Field>
            <Field label="Customer code">{isProspect ? "New customer - not yet created" : visit.custCode ?? null}</Field>
          </div>
        </CollapsibleCard>

        {/* Batch212: a prospect typed in on the visit. The office creates the
            real customer when it quotes, from these details. */}
        {isProspect && (
          <CollapsibleCard
            title="New customer"
            icon={UserPlus}
            accent={PANEL_ACCENT.link}
            hasData
            defaultOpen
            summary={[visit.prospectPhone, visit.prospectArea].filter(Boolean).join(" · ") || undefined}
          >
            <div className="row">
              <Field label="Name">{visit.prospectName}</Field>
              <Field label="Phone">{visit.prospectPhone}</Field>
              <Field label="Area / location">{visit.prospectArea}</Field>
            </div>
          </CollapsibleCard>
        )}

        <CollapsibleCard
          title="Requirement (for quotation)"
          icon={FileText}
          accent={PANEL_ACCENT.work}
          hasData={hasRequirement}
          defaultOpen={hasRequirement}
          count={needs.length || undefined}
          summary={needs.length ? needs.join(", ") : undefined}
        >
          <div className="mb-2 d-flex flex-wrap gap-2">
            {needs.length === 0 ? (
              <span style={{ color: INK_MUTED, fontSize: 13 }}>Nothing ticked.</span>
            ) : (
              needs.map((n) => (
                <span
                  key={n}
                  style={{
                    fontSize: 11.5, fontWeight: 700, color: PANEL_ACCENT.work,
                    background: `${PANEL_ACCENT.work}14`,
                    border: `1px solid ${PANEL_ACCENT.work}3D`,
                    borderRadius: 999, padding: "3px 10px",
                  }}
                >
                  {n}
                </span>
              ))
            )}
          </div>
          {visit.requirementDetails ? <TextBlock>{visit.requirementDetails}</TextBlock> : null}
        </CollapsibleCard>

        <CollapsibleCard
          title="Who was met"
          icon={User}
          accent={PANEL_ACCENT.link}
          hasData={hasContact}
          summary={hasContact ? [visit.contactPerson, visit.contactDesignation].filter(Boolean).join(" · ") : undefined}
        >
          <div className="row">
            <Field label="Contact person">{visit.contactPerson}</Field>
            <Field label="Designation">{visit.contactDesignation}</Field>
            <Field label="Mobile no.">{visit.contactMobile}</Field>
          </div>
        </CollapsibleCard>

        <CollapsibleCard title="Remarks" icon={Edit3} accent={PANEL_ACCENT.document} hasData defaultOpen>
          <TextBlock>{visit.remarks || "—"}</TextBlock>
          {visit.clientRemarks ? (
            <>
              <div className="mt-3 mb-1" style={{ fontSize: 11.5, fontWeight: 700, color: INK_MUTED }}>
                CLIENT&apos;S OWN REMARKS
              </div>
              <TextBlock tint="#FFFDF5">{visit.clientRemarks}</TextBlock>
            </>
          ) : null}
        </CollapsibleCard>

        <CollapsibleCard
          title="1. Guarding"
          icon={Shield}
          accent={PANEL_ACCENT.guards}
          hasData={hasGuarding}
          count={totalGuards ?? undefined}
          summary={hasGuarding && totalGuards != null ? `${totalGuards} on site` : undefined}
        >
          <div className="row">
            <BigCount label="Guards — day" value={visit.guardsDay} />
            <BigCount label="Guards — night" value={visit.guardsNight} />
            <BigCount label="Dog & handler" value={visit.dogAndHandler} />
            {/* Batch182c: the guard in charge is a PERSON, not a count, so
                they get a named badge rather than a figure in the count row. */}
            <div className="col-6 col-md-3 mb-2">
              <div style={{ fontSize: 11, color: INK_MUTED }}>Guard in charge</div>
              {visit.guardInChargeName ? (
                <span
                  className="d-inline-flex align-items-center gap-1"
                  style={{
                    fontSize: 12, fontWeight: 700, color: PANEL_ACCENT.guards,
                    background: `${PANEL_ACCENT.guards}14`,
                    border: `1px solid ${PANEL_ACCENT.guards}3D`,
                    borderRadius: 999, padding: "3px 9px", marginTop: 2,
                  }}
                  title={visit.guardInChargeEmpCode ? `Employee ${visit.guardInChargeEmpCode}` : undefined}
                >
                  <Award size={12} />
                  {visit.guardInChargeEmpCode
                    ? `${visit.guardInChargeEmpCode} — ${visit.guardInChargeName}`
                    : visit.guardInChargeName}
                </span>
              ) : (
                <div style={{ fontSize: 20, fontWeight: 800, color: INK_MUTED, lineHeight: 1.2 }}>—</div>
              )}
            </div>
          </div>
          <div className="row mt-1">
            <Field label="Uniform"><Rating value={visit.uniformRating} /></Field>
            <Field label="Appearance"><Rating value={visit.appearanceRating} /></Field>
            <Field label="Presentation"><Rating value={visit.presentationRating} /></Field>
            <Field label="Security coverage">
              {visit.guardingCoverage ? COVERAGE_LABEL[visit.guardingCoverage] || visit.guardingCoverage : null}
            </Field>
          </div>
          {visit.guardingComments ? <TextBlock>{visit.guardingComments}</TextBlock> : null}
        </CollapsibleCard>

        <CollapsibleCard
          title="2. Alarm"
          icon={Bell}
          accent={PANEL_ACCENT.services}
          hasData={hasAlarm}
          count={devices.length || undefined}
          summary={devices.length ? devices.join(", ") : undefined}
        >
          <div className="mb-2 d-flex flex-wrap gap-2">
            {devices.length === 0 ? (
              <span style={{ color: INK_MUTED, fontSize: 13 }}>No alarm equipment recorded.</span>
            ) : (
              devices.map((d) => (
                <span
                  key={d}
                  style={{
                    fontSize: 11.5, fontWeight: 700, color: PANEL_ACCENT.services,
                    background: `${PANEL_ACCENT.services}14`,
                    border: `1px solid ${PANEL_ACCENT.services}3D`,
                    borderRadius: 999, padding: "3px 10px",
                  }}
                >
                  {d}
                </span>
              ))
            )}
          </div>
          <div className="row">
            <Field label="Others">{visit.alarmOthers}</Field>
            <Field label="Quality of installation"><Rating value={visit.installationQualityRating} /></Field>
            <Field label="Security coverage"><Rating value={visit.alarmCoverageRating} /></Field>
          </div>
          {visit.alarmComments ? <TextBlock>{visit.alarmComments}</TextBlock> : null}
        </CollapsibleCard>

        <CollapsibleCard
          title="Where"
          icon={MapPin}
          accent={PANEL_ACCENT.link}
          hasData={hasCoords}
          summary={visit.accuracyMeters != null ? `±${Math.round(visit.accuracyMeters)} m` : undefined}
        >
          <div className="row align-items-center">
            <Field label="Coordinates" width="col-md-5">
              {hasCoords ? `${Number(visit.latitude).toFixed(6)}, ${Number(visit.longitude).toFixed(6)}` : null}
            </Field>
            <Field label="Accuracy" width="col-md-3">
              {visit.accuracyMeters != null ? `±${Math.round(visit.accuracyMeters)} m` : null}
            </Field>
            {mapUrl && (
              <div className="col-md-4 mb-2">
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm d-inline-flex align-items-center gap-1"
                  style={{
                    color: PANEL_ACCENT.link,
                    border: `1px solid ${PANEL_ACCENT.link}59`,
                    background: `${PANEL_ACCENT.link}0F`,
                    fontWeight: 700,
                  }}
                >
                  <Navigation size={13} /> Open in Maps
                </a>
              </div>
            )}
          </div>
        </CollapsibleCard>
      </Modal.Body>

      <Modal.Footer>
        {/* This is the sheet people actually print (user, 2026-09-18) - so the
            print action lives here, on the detail, not only on the list. */}
        <ClientVisitPrintButton visit={visit} />
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClientVisitDetailModal;
